import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getAdminMeetingsCard } from '@/api/meetingService';
import FeedbackReportModal from '@/components/Cards/FeedbackReportModal';
import { useDashboardTheme } from './themeHelper';
import { isMeetingRecorded } from '@/utils/meetingFilter';

// ─── Types ─────────────────────────────────────────────────────────────────

type SlimMeeting = {
  id: string;
  salesRep: string;
  salesRepRole: 'admin' | 'manager' | 'user';
  company: string;
  customer: string; // Add customer field
  industry: string;
  businessModel: string;
  decisionProcess: string;
  techStack: string;
  status: string;
  overallScore: number;
  createdAt: string;
  durationMinutes: number;
  meetingType: string;
  isRecorded: boolean;       // true when TargetCompanyProfile is null
  audioUrl: string;
  meetingPurpose: string[];
  aiData: {
    summary: string[];
    insights: string[];
    suggestions: string[];
    justification: string[];
    categories: any[];
  };
  talkRatio: { agent: number; customer: number };
  raw?: any;
};

// ─── Config ────────────────────────────────────────────────────────────────

const typeConfig: Record<string, { label: string; color: string }> = {
  call:        { label: 'Calls',             color: '#38bdf8' },
  demo:        { label: 'Demos',             color: '#a78bfa' },
  negotiation: { label: 'Negotiations',      color: '#f59e0b' },
  objection:   { label: 'Objections',        color: '#f87171' },
  recorded:    { label: 'Recorded Meetings', color: '#2dd4bf' },
};

const statusConfig: Record<string, { dot: string; color: string }> = {
  Active:     { dot: '#34d399', color: '#34d399' },
  Processing: { dot: '#38bdf8', color: '#38bdf8' },
  Completed:  { dot: '#a78bfa', color: '#a78bfa' },
  Stalled:    { dot: '#f87171', color: '#f87171' },
};

// ─── Icons ─────────────────────────────────────────────────────────────────

const IconCall = () => (
  <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 5.75A2.75 2.75 0 015.75 3h.938a1 1 0 01.97.757l.918 3.674a1 1 0 01-.48 1.118L6.3 9.524a11.047 11.047 0 005.176 5.176l1.975-1.796a1 1 0 011.118-.48l3.674.918A1 1 0 0119 14.312v.938A2.75 2.75 0 0116.25 18C9.484 18 3 11.516 3 4.75v-1z" />
  </svg>
);
const IconDemo = () => (
  <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25A2.25 2.25 0 015.25 3h13.5A2.25 2.25 0 0121 5.25z" />
  </svg>
);
const IconNego = () => (
  <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
  </svg>
);
const IconObj = () => (
  <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
  </svg>
);
const IconRecorded = () => (
  <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
  </svg>
);

const TypeIcon = ({ type }: { type: string }) => {
  switch (type) {
    case 'demo':        return <IconDemo />;
    case 'negotiation': return <IconNego />;
    case 'objection':   return <IconObj />;
    case 'recorded':    return <IconRecorded />;
    default:            return <IconCall />;
  }
};

// ─── Helper: is recorded ───────────────────────────────────────────────────

const isRecordedMeeting = (raw: any): boolean => {
  const rawType = String(raw.meetingType || raw.otherDetails?.meetingType || raw.meeting_type || '').trim().toLowerCase();
  if (rawType && rawType !== 'recorded') return false;
  return !raw.TargetCompanyProfile || !raw.TargetCompanyProfile.id;
};

// ─── Transform ─────────────────────────────────────────────────────────────

// ─── Per-field max values (API schema) ───────────────────────────────────────
const FIELD_MAX: Record<string, number> = {
  captured_attention:          4,
  stated_purpose:              3,
  established_relevance:       3,
  asked_relevant_questions:    3,
  listened_actively:           3,
  identified_needs:            3,
  confirmed_understanding:     3,
  communicated_value:          3,
  structured_pitch:            3,
  used_clear_language:         3,
  kept_customer_engaged:       3,
  handled_objections:          3,
  asked_for_feedback:          3,
  sounded_confident:           4,
  used_positive_language:      3,
  maintained_energy:           3,
  summarized_key_points:       3,
  asked_for_next_step:         3,
  created_urgency:             3,
  professionalism:             4,
  rapport_building:            3,
  alignment_with_customer:     3,
  adaptability:                3,
  meeting_objective_achieved:  3,
};

const buildCategories = (ai_score: any) => {
  if (!ai_score) return [];
  const sections = [
    { key: 'opening',    label: 'Opening',            color: '#a78bfa', src: ai_score.opening },
    { key: 'discovery',  label: 'Discovery',          color: '#38bdf8', src: ai_score.discovery },
    { key: 'pitch',      label: 'Pitch Delivery',     color: '#34d399', src: ai_score.pitch_delivery },
    { key: 'engagement', label: 'Engagement',         color: '#f59e0b', src: ai_score.engagement },
    { key: 'confidence', label: 'Confidence & Tone',  color: '#fb923c', src: ai_score.confidence_and_tone },
    { key: 'closing',    label: 'Closing',            color: '#f472b6', src: ai_score.closing },
    { key: 'impression', label: 'Overall Impression', color: '#60a5fa', src: ai_score.overall_impression },
  ];
  return sections
    .filter((s) => s.src && typeof s.src === 'object')
    .map((s) => ({
      key: s.key, label: s.label, color: s.color,
      metrics: Object.entries(s.src)
        .filter(([, v]) => typeof v === 'number')
        .map(([k, v]) => ({
          label: k.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()),
          score: Number(v), max: (FIELD_MAX[k] ?? 3),
        })),
    }))
    .filter((c) => c.metrics.length > 0);
};

const transformSlim = (raw: any): SlimMeeting => {
  const profile   = raw.TargetCompanyProfile || {};
  const userName  = raw.User?.name || raw.user?.name || 'Unknown';
  const userRole  = raw.User?.role || raw.user?.role || 'user';
  const rawType   = String(raw.meetingType || raw.otherDetails?.meetingType || '').trim().toLowerCase();
  const isRecorded = isRecordedMeeting(raw);
  const ai_score   = raw.ai_score || {};
  const aiSummaryObj = raw.ai_summary || {};

  return {
    id:              raw.id || raw._id || '',
    salesRep:        userName,
    salesRepRole:    userRole,
    company:         profile.companyName || raw.companyName || raw.customerData?.companyName || '',
    customer:        raw.customerContact?.firstName && raw.customerContact?.lastName
      ? `${raw.customerContact.firstName} ${raw.customerContact.lastName}`.trim()
      : raw.customerContact?.email || raw.customerData?.companyName || '',
    industry:        profile.industry    || raw.customerData?.industry || '',
    businessModel:   profile.businessModel   || '',
    decisionProcess: profile.decisionProcess || '',
    techStack:       profile.techStack       || '',
    status: raw.status
      ? raw.status.charAt(0).toUpperCase() + raw.status.slice(1).toLowerCase()
      : 'Active',
    overallScore:    Math.min(100, raw.overallPercentage || raw.score || 0),
    createdAt:       raw.createdAt || raw.created_at || '',
    durationMinutes: raw.durationMinutes || 0,
    meetingType:     rawType || 'call',
    isRecorded,
    audioUrl:        raw.uploaded_audio_url || raw.s3Url || '',
    meetingPurpose:  raw.otherDetails?.meetingPurpose || [],
    aiData: {
      summary:       Array.isArray(aiSummaryObj.summary)     ? aiSummaryObj.summary     : (Array.isArray(raw.summary)     ? raw.summary     : []),
      insights:      Array.isArray(aiSummaryObj.insights)    ? aiSummaryObj.insights    : (Array.isArray(raw.insights)    ? raw.insights    : []),
      suggestions:   Array.isArray(aiSummaryObj.suggestions) ? aiSummaryObj.suggestions : (Array.isArray(raw.suggestions) ? raw.suggestions : []),
      justification: Array.isArray(ai_score.justification)   ? ai_score.justification   : [],
      categories:    buildCategories(ai_score),
    },
    talkRatio: { agent: raw.talkRatio?.agent || 60, customer: raw.talkRatio?.customer || 40 },
    raw,
  };
};

// ─── Shimmer ───────────────────────────────────────────────────────────────

const ShimmerSlim = () => (
  <div className="rounded-xl border border-white/[0.05] bg-[#1A1726] px-4 py-3.5 flex items-center gap-3 animate-pulse">
    <div className="w-9 h-9 rounded-lg bg-white/[0.07] flex-shrink-0" />
    <div className="flex-1 space-y-2">
      <div className="h-3 w-40 rounded-full bg-white/[0.07]" />
      <div className="h-2 w-28 rounded-full bg-white/[0.05]" />
    </div>
    <div className="h-8 w-28 rounded-lg bg-white/[0.07]" />
  </div>
);

// ─── Slim Card (Normal meetings) ───────────────────────────────────────────

const SlimCard: React.FC<{
  meeting: SlimMeeting;
  color: string;
  onNavigate: (id: string) => void;
  onFeedback: (m: SlimMeeting) => void;
}> = ({ meeting, color, onNavigate, onFeedback }) => {
  const scoreColor = meeting.overallScore >= 90 ? '#4ADE80' : meeting.overallScore >= 70 ? '#A3E635' : meeting.overallScore >= 50 ? '#FACC15' : '#F87171';
  const stCfg = statusConfig[meeting.status] || statusConfig['Active'];

  return (
    <div
      className="group flex items-center gap-3 rounded-xl border border-white/[0.06] bg-[#1A1726] px-4 py-3.5 transition-all duration-200 hover:border-white/[0.1] hover:bg-[#1D1A2E] cursor-pointer"
      onClick={() => onNavigate(meeting.id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onNavigate(meeting.id); } }}
    >
      {/* Score ring */}
      <div className="relative flex-shrink-0 w-10 h-10">
        <svg width="40" height="40" viewBox="0 0 40 40">
          <circle cx="20" cy="20" r="16" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="2.5" />
          <circle cx="20" cy="20" r="16" fill="none" stroke={scoreColor} strokeWidth="2.5"
            strokeDasharray={`${(meeting.overallScore / 100) * 100.53} 100.53`}
            strokeLinecap="round" transform="rotate(-90 20 20)" />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-[11px] font-bold text-white">{meeting.overallScore}</span>
        </div>
      </div>

      {/* Info */}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[14px] font-semibold text-white truncate">{meeting.salesRep}</span>
          <span className="flex items-center gap-1 rounded-full px-1.5 py-[2px] text-[10px] font-medium"
            style={{ background: `${stCfg.color}12`, color: stCfg.color, border: `1px solid ${stCfg.color}25` }}>
            <span className="w-1 h-1 rounded-full" style={{ background: stCfg.dot }} />
            {meeting.status}
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
          {meeting.company && <span className="text-[13px] font-medium text-gray-300 truncate">{meeting.company}</span>}
          {meeting.company && meeting.industry && <span className="text-gray-700 text-xs">·</span>}
          {meeting.industry && <span className="text-[12px] text-gray-500">{meeting.industry}</span>}
          {meeting.businessModel && <><span className="text-gray-700 text-xs">·</span><span className="text-[11px] bg-white/[0.05] text-gray-500 rounded px-1.5 py-px">{meeting.businessModel}</span></>}
          {meeting.createdAt && <><span className="text-gray-700 text-xs">·</span><span className="text-[12px] text-gray-600">{new Date(meeting.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span></>}
          {meeting.durationMinutes > 0 && <><span className="text-gray-700 text-xs">·</span><span className="text-[12px] text-gray-600">{meeting.durationMinutes}m</span></>}
        </div>

        {(meeting.techStack || meeting.decisionProcess) && (
          <div className="flex items-center gap-1.5 mt-0.5">
            {meeting.techStack && <span className="text-[11px] text-gray-600">Stack: <span className="text-gray-500">{meeting.techStack}</span></span>}
            {meeting.techStack && meeting.decisionProcess && <span className="text-gray-700 text-[9px]">·</span>}
            {meeting.decisionProcess && <span className="text-[11px] text-gray-600">Decision: <span className="text-gray-500">{meeting.decisionProcess}</span></span>}
          </div>
        )}

        {meeting.meetingPurpose.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {meeting.meetingPurpose.slice(0, 3).map((p, i) => (
              <span key={i} className="text-[10px] rounded-full px-1.5 py-px font-medium"
                style={{ background: `${color}10`, color: `${color}bb`, border: `1px solid ${color}20` }}>
                {p.replace(/_/g, ' ')}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* View Feedback */}
      <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={() => onFeedback(meeting)}
          className="flex items-center gap-1.5 rounded-xl px-3.5 py-2 text-[13px] font-semibold transition-all hover:opacity-80 border"
          style={{ background: `${color}12`, color, borderColor: `${color}28` }}
        >
          <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <span className="hidden sm:inline">View Feedback</span>
        </button>
      </div>
    </div>
  );
};

// ─── Recorded Card ─────────────────────────────────────────────────────────

const RecordedCard: React.FC<{
  meeting: SlimMeeting;
  onNavigate: (id: string) => void;
  onFeedback: (m: SlimMeeting) => void;
}> = ({ meeting, onNavigate, onFeedback }) => {
  const color = '#2dd4bf';
  const scoreColor = meeting.overallScore >= 90 ? '#4ADE80' : meeting.overallScore >= 70 ? '#A3E635' : meeting.overallScore >= 50 ? '#FACC15' : '#F87171';
  const stCfg = statusConfig[meeting.status] || statusConfig['Active'];
  const fileName = meeting.audioUrl ? meeting.audioUrl.split('/').pop() : null;

  return (
    <div
      className="group flex items-center gap-3 rounded-xl border border-white/[0.06] bg-[#1A1726] px-4 py-3.5 transition-all duration-200 hover:border-[#2dd4bf]/20 hover:bg-[#1D1A2E] cursor-pointer"
      onClick={() => onNavigate(meeting.id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onNavigate(meeting.id); } }}
    >
      {/* Score ring */}
      <div className="relative flex-shrink-0 w-10 h-10">
        <svg width="40" height="40" viewBox="0 0 40 40">
          <circle cx="20" cy="20" r="16" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="2.5" />
          <circle cx="20" cy="20" r="16" fill="none" stroke={scoreColor} strokeWidth="2.5"
            strokeDasharray={`${(meeting.overallScore / 100) * 100.53} 100.53`}
            strokeLinecap="round" transform="rotate(-90 20 20)" />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-[11px] font-bold text-white">{meeting.overallScore}</span>
        </div>
      </div>

      {/* Info */}
      <div className="min-w-0 flex-1">
        {/* Row 1 */}
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[14px] font-semibold text-white truncate">{meeting.salesRep}</span>
          {/* Recorded badge */}
          <span className="flex items-center gap-1 rounded-full px-1.5 py-[2px] text-[10px] font-medium"
            style={{ background: 'rgba(45,212,191,0.1)', color: '#2dd4bf', border: '1px solid rgba(45,212,191,0.2)' }}>
            <IconRecorded />
            Recorded
          </span>
          <span className="flex items-center gap-1 rounded-full px-1.5 py-[2px] text-[10px] font-medium"
            style={{ background: `${stCfg.color}12`, color: stCfg.color, border: `1px solid ${stCfg.color}25` }}>
            <span className="w-1 h-1 rounded-full" style={{ background: stCfg.dot }} />
            {meeting.status}
          </span>
        </div>

        {/* Row 2 — audio file name + date */}
        <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
          {fileName && (
            <span className="text-[12px] text-[#2dd4bf]/70 font-mono truncate max-w-[200px]">{fileName}</span>
          )}
          {meeting.createdAt && (
            <>
              <span className="text-gray-700 text-xs">·</span>
              <span className="text-[12px] text-gray-600">
                {new Date(meeting.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </span>
            </>
          )}
          {meeting.durationMinutes > 0 && (
            <>
              <span className="text-gray-700 text-xs">·</span>
              <span className="text-[12px] text-gray-600">{meeting.durationMinutes}m</span>
            </>
          )}
        </div>

        {/* Row 3 — AI summary preview if available */}
        {meeting.aiData.summary.length > 0 && (
          <p className="text-[12px] text-gray-500 mt-1 line-clamp-1">
            {meeting.aiData.summary[0]}
          </p>
        )}
      </div>

      {/* View Feedback */}
      <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={() => onFeedback(meeting)}
          className="flex items-center gap-1.5 rounded-xl px-3.5 py-2 text-[13px] font-semibold transition-all hover:opacity-80 border"
          style={{ background: `${color}12`, color, borderColor: `${color}28` }}
        >
          <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <span className="hidden sm:inline">View Feedback</span>
        </button>
      </div>
    </div>
  );
};

// ─── Page ──────────────────────────────────────────────────────────────────

const MeetingTypeListPage: React.FC = () => {
  const { isDark, colors } = useDashboardTheme();
  const { userId, type } = useParams<{ userId: string; type: string }>();
  const navigate = useNavigate();

  const isRecordedPage = (type || '').toLowerCase() === 'recorded';
  const config         = typeConfig[type || ''] || typeConfig.call;

  const [rawMeetings,      setRawMeetings]      = useState<any[]>([]);
  const [loading,          setLoading]          = useState(true);
  const [error,            setError]            = useState<string | null>(null);
  const [searchQuery,      setSearchQuery]      = useState('');
  const [companyFilter,    setCompanyFilter]    = useState('');
  const [customerFilter,   setCustomerFilter]   = useState('');
  const [selectedFeedback, setSelectedFeedback] = useState<SlimMeeting | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true); setError(null);
        const res: any = await getAdminMeetingsCard({ view: 'list', page: 1, pageSize: 1000 });
        const rawList: any[] = (Array.isArray(res?.data?.allMeetings)
          ? res.data.allMeetings
          : Array.isArray(res?.data?.items) ? res.data.items : []).filter(isMeetingRecorded);

        const byUser = userId
          ? rawList.filter((m: any) => {
              const uid = m.User?.id || m.user?.id || m.userId || m.user_id || m.createdBy;
              return uid === userId;
            })
          : rawList;

        setRawMeetings(byUser);
      } catch (e) {
        console.error(e);
        setError('Failed to load meetings. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [userId]);

  const meetings = useMemo(() => rawMeetings.map(transformSlim), [rawMeetings]);

  // Company options — for recorded page show all companies (they may have none), for normal show companies
  const companyOptions = useMemo(() => {
    const base = isRecordedPage
      ? meetings.filter((m) => m.isRecorded)
      : meetings;
    return [...new Set(base.map((m) => m.company).filter((c) => c && c !== 'Unknown'))].sort();
  }, [meetings, isRecordedPage]);

  // Customer options — only when company is selected
  const customerOptions = useMemo(() => {
    if (!companyFilter) return [];
    const base = isRecordedPage
      ? meetings.filter((m) => m.isRecorded)
      : meetings;
    return [...new Set(base
      .filter((m) => m.company === companyFilter)
      .map((m) => m.customer)
      .filter((c) => c)
    )].sort();
  }, [meetings, companyFilter, isRecordedPage]);

  const filtered = useMemo(() => {
    const t = (type || '').toLowerCase();
    return meetings.filter((m) => {
      // Recorded page: only show meetings where TargetCompanyProfile is null
      if (isRecordedPage) {
        if (!m.isRecorded) return false;
      } else {
        // Normal pages: exclude recorded meetings, filter by type
        if (m.isRecorded) return false;
        if (t && t !== 'recorded' && m.meetingType !== t) return false;
      }

      if (companyFilter && m.company !== companyFilter) return false;
      if (customerFilter && m.customer !== customerFilter) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (
          !m.salesRep.toLowerCase().includes(q) &&
          !m.company.toLowerCase().includes(q) &&
          !m.customer.toLowerCase().includes(q) &&
          !m.industry.toLowerCase().includes(q)
        ) return false;
      }
      return true;
    });
  }, [meetings, searchQuery, companyFilter, customerFilter, type, isRecordedPage]);

  return (
    <main className={"flex-1 p-4 md:p-6 mt-15 min-h-screen " + (isDark ? "" : "light-theme-dashboard")} style={{ background: isDark ? '#13111C' : colors.bg }}>

      {/* ── Header ── */}
      <div className="flex items-center gap-3 mb-5">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 rounded-xl border border-white/[0.07] bg-white/[0.03] px-3 py-2 text-[13px] text-gray-500 hover:text-gray-200 hover:bg-white/[0.05] transition-colors"
        >
          <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M11 17l-5-5m0 0l5-5m-5 5h12" />
          </svg>
          Back
        </button>
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center border"
            style={{ background: `${config.color}10`, borderColor: `${config.color}25`, color: config.color }}>
            <TypeIcon type={type || 'call'} />
          </div>
          <div>
            <h1 className="text-[18px] font-bold text-white tracking-tight leading-none">{config.label}</h1>
            <p className="text-[12px] text-gray-600 mt-0.5">
              {loading ? '—' : `${filtered.length} session${filtered.length !== 1 ? 's' : ''}`}
              {userId ? ' · this rep' : ''}
            </p>
          </div>
        </div>
      </div>

      {/* ── Error ── */}
      {error && (
        <div className="mb-4 flex items-center gap-2.5 p-3 bg-rose-500/8 border border-rose-500/18 rounded-xl text-rose-400 text-[13px]">
          <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M12 3a9 9 0 100 18A9 9 0 0012 3z" />
          </svg>
          {error}
          <button onClick={() => window.location.reload()} className="ml-auto text-[11px] border border-rose-500/25 rounded-full px-2.5 py-1 hover:bg-rose-500/10">Retry</button>
        </div>
      )}

      {/* ── Filters ── */}
      <div className="flex gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[160px]">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
            width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="rgba(255,255,255,0.2)" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" />
          </svg>
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={`Search ${config.label.toLowerCase()}…`}
            className="w-full rounded-lg bg-[#1E1B2E] border border-white/[0.07] text-[14px] text-white placeholder-gray-700 pl-8 pr-3 py-2.5 outline-none focus:border-white/[0.18] transition-colors"
          />
        </div>
        {companyOptions.length > 0 && (
          <select
            value={companyFilter}
            onChange={(e) => { setCompanyFilter(e.target.value); setCustomerFilter(''); }}
            className="rounded-lg bg-[#1E1B2E] border border-white/[0.07] text-[14px] text-gray-400 px-3 py-2.5 outline-none cursor-pointer"
            style={{ minWidth: 150 }}
          >
            <option value="">All Companies</option>
            {companyOptions.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
        {companyFilter && customerOptions.length > 0 && (
          <select
            value={customerFilter}
            onChange={(e) => setCustomerFilter(e.target.value)}
            className="rounded-lg bg-[#1E1B2E] border border-white/[0.07] text-[14px] text-gray-400 px-3 py-2.5 outline-none cursor-pointer"
            style={{ minWidth: 150 }}
          >
            <option value="">All Customers</option>
            {customerOptions.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
        {(searchQuery || companyFilter || customerFilter) && (
          <button
            onClick={() => { setSearchQuery(''); setCompanyFilter(''); setCustomerFilter(''); }}
            className="rounded-lg bg-white/[0.03] border border-white/[0.07] text-[13px] text-gray-600 px-3 py-2.5 hover:text-gray-300 transition-colors"
          >
            Clear
          </button>
        )}
      </div>

      {/* ── Cards ── */}
      <div className="grid gap-2">
        {loading
          ? Array.from({ length: 5 }).map((_, i) => <ShimmerSlim key={i} />)
          : !filtered.length
            ? (
              <div className="flex flex-col items-center justify-center h-44 rounded-2xl border border-dashed border-white/[0.05] bg-[#1A1726] gap-2.5">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center border opacity-30"
                  style={{ background: `${config.color}10`, borderColor: `${config.color}25`, color: config.color }}>
                  <TypeIcon type={type || 'call'} />
                </div>
                <p className="text-gray-600 text-[14px]">No {config.label.toLowerCase()} found</p>
                {(searchQuery || companyFilter || customerFilter) && (
                  <button onClick={() => { setSearchQuery(''); setCompanyFilter(''); setCustomerFilter(''); }}
                    className="text-[12px] text-gray-600 hover:text-gray-400 underline underline-offset-2 transition">
                    Clear filters
                  </button>
                )}
              </div>
            )
            : filtered.map((meeting) =>
              isRecordedPage ? (
                <RecordedCard
                  key={meeting.id}
                  meeting={meeting}
                  onNavigate={(id) => navigate(`/meeting-detail/${id}`, { state: { meeting: meeting.raw || meeting, meetingId: id, from: location.pathname + location.search } })}
                  onFeedback={setSelectedFeedback}
                />
              ) : (
                <SlimCard
                  key={meeting.id}
                  meeting={meeting}
                  color={config.color}
                  onNavigate={(id) => navigate(`/meeting-detail/${id}`, { state: { meeting: meeting.raw || meeting, meetingId: id, from: location.pathname + location.search } })}
                  onFeedback={setSelectedFeedback}
                />
              )
            )
        }
      </div>

      {/* ── Feedback Modal ── */}
      {selectedFeedback && (
        <FeedbackReportModal
          open={!!selectedFeedback}
          onClose={() => setSelectedFeedback(null)}
          meeting={selectedFeedback}
        />
      )}
    </main>
  );
};

export default MeetingTypeListPage;